"use client"

import { useState, useEffect, useRef } from "react"
import { Menu, BarChart2, User, Star, LogOut, Sparkles } from "lucide-react"
import { useSelector, useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import "../styles/Header.css"
import { logout as logoutAction } from "../../../redux/slices/authSlice"
import { useLogoutMutation } from "../../../redux/slices/api/authApiSlice"
import { useAuthModal } from "../../auth/AuthModalContext"
import { useWhatsNew } from "../../announcements/WhatsNewContext"

function Header({ toggleSidebar, sidebarOpen }) {
  const [scrolled, setScrolled] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef(null)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user } = useSelector((state) => state.auth)
  const [logoutApi] = useLogoutMutation()
  const { openAuth } = useAuthModal()
  const { openWhatsNew } = useWhatsNew()
  const isAuthenticated = !!user

  const getInitials = (name) => (name ? name.charAt(0).toUpperCase() : "U")

  const getAvatarColor = (name) => {
    const colors = ["#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7"]
    return colors[name ? name.length % colors.length : 0]
  }

  const handleLogout = async () => {
    try {
      await logoutApi().unwrap()
    } catch (err) {
      // ignore network/logout errors — clear client state regardless
    }
    dispatch(logoutAction())
    setDropdownOpen(false)
    navigate("/homepage")
  }

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10)
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  return (
    <header className={`header ${sidebarOpen ? "sidebar-open" : ""} ${scrolled ? "header-scrolled" : ""}`}>
      <button className="hamburger-button" onClick={toggleSidebar} aria-label="Toggle navigation">
        <Menu size={20} />
      </button>

      <div className="header-logo">
        <div className="logo-icon-wrap">
          <BarChart2 size={18} />
        </div>
        <span className="logo-dsa">DSA</span>
        <span className="logo-separator">·</span>
        <span className="logo-visualizer">Visualizer</span>
      </div>

      <div className="avatar-section">
        <button
          className="whatsnew-trigger"
          onClick={openWhatsNew}
          aria-label="What's New"
          title="What's New"
        >
          <Sparkles size={18} />
        </button>

        {isAuthenticated ? (
          <div className="avatar-container" ref={dropdownRef}>
            <button
              className="avatar-button authenticated"
              style={{ background: getAvatarColor(user?.name) }}
              onClick={() => setDropdownOpen((o) => !o)}
              aria-label="Account menu"
            >
              {getInitials(user?.name)}
            </button>

            {dropdownOpen && (
              <div className="avatar-dropdown">
                <div className="dropdown-header">
                  <div className="user-info">
                    <div
                      className="dropdown-avatar"
                      style={{ background: getAvatarColor(user?.name) }}
                    >
                      {getInitials(user?.name)}
                    </div>
                    <div className="user-details">
                      <span className="username">{user?.name || "User"}</span>
                      <span className="user-status">Online</span>
                    </div>
                  </div>
                </div>

                <div className="dropdown-divider"></div>

                <button
                  className="dropdown-item"
                  onClick={() => {
                    setDropdownOpen(false)
                    navigate("/my-space")
                  }}
                >
                  <Star size={16} /> My Space
                </button>

                <button className="dropdown-item logout-item" onClick={handleLogout}>
                  <LogOut size={16} /> Logout
                </button>
              </div>
            )}
          </div>
        ) : (
          <button
            className="avatar-button login-avatar"
            aria-label="Login"
            onClick={() => openAuth("login")}
          >
            <User size={18} />
          </button>
        )}
      </div>
    </header>
  )
}

export default Header
